// ApiPerformanceLogListScreen.jsx
import React, { useState } from "react";
import {
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useGetApiPerformanceLogsQuery } from "store/services/apiPerformanceLogApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useTablePagination } from "hooks";

const LEVEL_OPTIONS = [
    { value: "", label: "All" },
    { value: "warn", label: "Warn" },
    { value: "critical", label: "Critical" },
];

export const ApiPerformanceLogListScreen = () => {
    const breadcrumbs = [{
        title: "Reports",
        path: "/app/crm/reports/list",
    }, { title: "API Performance Logs" }];

    const [level, setLevel] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortDirection, setSortDirection] = useState(-1); // -1 = desc, 1 = asc

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination(10);

    const { data, isLoading, isFetching } = useGetApiPerformanceLogsQuery({
        level: level || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: currentPage,
        limit: perPage,
        sortBy,
        sortDirection,
    });

    const handleLevelChange = (e) => {
        setLevel(e.target.value);
        handlePageChange(1);
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        handlePageChange(1);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        handlePageChange(1);
    };

    const handleClearDates = () => {
        setStartDate("");
        setEndDate("");
        handlePageChange(1);
    };

    // react-data-table-component: fires with (column, sortDirection)
    const handleSort = (column, direction) => {
        if (!column.sortField) return;
        setSortBy(column.sortField);
        setSortDirection(direction === "asc" ? 1 : -1);
        handlePageChange(1);
    };

    const columns = [
        {
            name: "Endpoint",
            selector: (row) => row.endpoint,
            cell: (row) => (
                <span style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {row.endpoint}
                </span>
            ),
        },
        {
            name: "Query / Params",
            selector: (row) => JSON.stringify(row.query) + JSON.stringify(row.params),
            cell: (row) => {
                const hasQuery = row.query && Object.keys(row.query).length > 0;
                const hasParams = row.params && Object.keys(row.params).length > 0;
                if (!hasQuery && !hasParams) {
                    return (
                        <Typography variant="caption" color="text.secondary">
                            —
                        </Typography>
                    );
                }
                return (
                    <Box sx={{ fontFamily: "monospace", fontSize: "0.7rem", maxWidth: 260, wordBreak: "break-word" }}>
                        {hasParams && (
                            <Box>
                                <strong>params:</strong> {JSON.stringify(row.params)}
                            </Box>
                        )}
                        {hasQuery && (
                            <Box>
                                <strong>query:</strong> {JSON.stringify(row.query)}
                            </Box>
                        )}
                    </Box>
                );
            },
        },
        {
            name: "Method",
            selector: (row) => row.method,
        },
        {
            name: "User",
            selector: (row) => row.userId?.name,
            cell: (row) => row.userId?.name || "N/A",
        },
        {
            name: "Duration",
            selector: (row) => row.duration,
            sortable: true,
            sortField: "duration",
            cell: (row) => `${row.duration} ms`,
        },
        {
            name: "Status",
            selector: (row) => row.level,
            cell: (row) => (
                <Chip
                    label={row.level}
                    size="small"
                    color={row.level === "critical" ? "error" : "warning"}
                    variant="filled"
                />
            ),
            center: true,
        },
        {
            name: "HTTP Status",
            selector: (row) => row.statusCode,
            center: true,
        },
        {
            name: "Time",
            selector: (row) => row.createdAt,
            sortable: true,
            sortField: "createdAt",
            cell: (row) => new Date(row.createdAt).toLocaleString(),
        },
    ];

    const logs = data?.data || [];
    const total = data?.total || 0;

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Filters */}
                        <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3} sx={{ mb: 2, mr: { sm: 2 } }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="level-filter-label">Status</InputLabel>
                                        <Select
                                            labelId="level-filter-label"
                                            label="Status"
                                            value={level}
                                            onChange={handleLevelChange}
                                        >
                                            {LEVEL_OPTIONS.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3} sx={{ mb: 2, mr: { sm: 2 } }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Start Date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3} sx={{ mb: 2, mr: { sm: 2 } }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="End Date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm="auto" sx={{ mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleClearDates}
                                        disabled={!startDate && !endDate}
                                        sx={{ height: 40 }}
                                    >
                                        Clear
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Table */}
                        <Grid item xs={12}>
                            <AppTable
                                columns={columns}
                                data={logs}
                                progressPending={isLoading || isFetching}
                                sortServer
                                onSort={handleSort}
                                paginationServer
                                paginationPerPage={perPage}
                                paginationTotalRows={total}
                                paginationDefaultPage={currentPage}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                            />
                            {isFetching && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Refreshing...
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};